/**
 * Safeguard for the greeting card widget of the Super Gift Options app (Effectify).
 *
 * The app saves the gift message, and adds its greeting card product, only when the message
 * textarea loses focus, with a request nothing waits for. Clicking "Zur Kasse" or an express
 * checkout button straight from the textarea leaves before that request lands, so the message and
 * the card are lost.
 *
 * This module:
 * - triggers the app's own save after a pause in typing, when the textarea loses focus, and when
 *   the box is ticked with text already in the field;
 * - locks the express checkout buttons while a save is pending. Wallets read the cart on the click
 *   itself, so they can't be held like the checkout submit;
 * - holds the native checkout submit, saves the message once more and continues once the cart
 *   contains it.
 *
 * Product decision: the safeguard may delay checkout but must never prevent it. Every save ends
 * after MAX_HOLD_MS at the latest, which unlocks the express buttons and continues a held
 * checkout, with or without a confirmed greeting card. If the app stops exposing its save
 * function, nothing is held. Either way the reason is logged.
 */

const CHECKBOX_ID = 'effectiveAppsEGW';
const MESSAGE_FIELD_ID = 'effectiveAppsEgwGiftMessageText';
const MESSAGE_ATTRIBUTE = 'Gift Message';
const EXPRESS_BUTTONS_SELECTOR = '.cart__ctas .additional-checkout-buttons';
const EXPRESS_LOCK_ATTRIBUTE = 'data-gift-message-saving';
/** ThemeEvents.cartUpdate, not imported: the theme's import map needs Safari 16.4. */
const CART_UPDATE_EVENT = 'cart:update';
const MAX_HOLD_MS = 5000;
const POLL_INTERVAL_MS = 250;
/** Pause in typing before the message is saved. */
const SAVE_WHILE_TYPING_DELAY_MS = 700;

let isHoldingCheckout = false;
/**
 * The message last confirmed in the cart on this page. Cleared while a save runs.
 * @type {string | null}
 */
let savedMessage = null;
/**
 * The save in progress. A newer save replaces it and stops its polling.
 * @type {{ message: string, controller: AbortController, promise: Promise<void> } | null}
 */
let currentSave = null;
/**
 * The save scheduled after a pause in typing.
 * @type {ReturnType<typeof setTimeout> | undefined}
 */
let typingTimer;

/**
 * @typedef {object} GiftMessageRequest
 * @property {string} message - The text the customer wants on the greeting card.
 * @property {() => void} save - The app's save function, which writes the message and the card line.
 */

/**
 * The greeting card message to save, or null when there is nothing to save.
 * @returns {GiftMessageRequest | null}
 */
function getGiftMessageRequest() {
  const checkbox = document.getElementById(CHECKBOX_ID);
  if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return null;

  const field = document.getElementById(MESSAGE_FIELD_ID);
  if (!(field instanceof HTMLTextAreaElement)) {
    console.error(`Gift message safeguard disabled: #${MESSAGE_FIELD_ID} from Super Gift Options is missing`);
    return null;
  }
  if (field.value === '') return null;

  const save = window.updateGiftMessageStatus;
  if (typeof save !== 'function') {
    console.error('Gift message safeguard disabled: updateGiftMessageStatus() from Super Gift Options is missing');
    return null;
  }

  return { message: field.value, save };
}

/**
 * Whether the cart contains the message, or null when the cart couldn't be read.
 * @param {string} message
 * @param {AbortSignal} signal
 * @returns {Promise<boolean | null>}
 */
async function cartHasMessage(message, signal) {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { signal });
    if (!response.ok) throw new Error(`Cart request failed with HTTP ${response.status}`);

    const cart = await response.json();
    return cart.attributes[MESSAGE_ATTRIBUTE] === message;
  } catch (error) {
    if (signal.aborted) throw signal.reason;

    console.error('Gift message safeguard: cart check failed', error);
    return null;
  }
}

/**
 * Triggers the app's save and resolves once the cart contains the message.
 * @param {GiftMessageRequest} request
 * @param {AbortController} controller - Aborted when a newer save replaces this one.
 * @param {boolean} isOtherTextInFlight - A save of other text is in flight. The cart may still
 *   hold this message from an earlier save, so it counts only after the cart has shown other text.
 */
async function saveMessage({ message, save }, controller, isOtherTextInFlight) {
  // A timer on the controller instead of AbortSignal.timeout(), which needs Safari 16; this works
  // from Safari 15.4.
  const timer = setTimeout(
    () => controller.abort(new Error(`Gift message not in the cart after ${MAX_HOLD_MS} ms`)),
    MAX_HOLD_MS
  );
  const { signal } = controller;

  try {
    save();
    let hasFailedCheck = false;
    let hasSeenOtherText = !isOtherTextInFlight;

    while (true) {
      const hasMessage = await cartHasMessage(message, signal);
      if (hasMessage && hasSeenOtherText) return;

      if (hasMessage === false) hasSeenOtherText = true;
      if (hasMessage === null) {
        // Retried once: a single failed check can be transient while the app's save is in flight.
        if (hasFailedCheck) throw new Error('Cart check failed twice');
        hasFailedCheck = true;
      }

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      signal.throwIfAborted();
    }
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Saves the message, replacing the save in progress.
 * @param {GiftMessageRequest} request
 * @returns {Promise<void>}
 */
function startSave(request) {
  const isOtherTextInFlight = currentSave !== null && currentSave.message !== request.message;
  if (currentSave !== null) currentSave.controller.abort(new Error('Replaced by a newer save'));

  const controller = new AbortController();
  const entry = {
    message: request.message,
    controller,
    promise: saveMessage(request, controller, isOtherTextInFlight),
  };
  currentSave = entry;
  savedMessage = null;
  updateExpressLock();

  entry.promise.then(
    () => {
      if (currentSave !== entry) return;

      currentSave = null;
      savedMessage = entry.message;
      updateExpressLock();
    },
    (error) => {
      // A replaced save was stopped on purpose; the newer one reports its own outcome.
      if (currentSave !== entry) return;

      currentSave = null;
      console.error('Gift message safeguard: message not confirmed in the cart, express checkout unlocked', error);
      updateExpressLock();
    }
  );

  return entry.promise;
}

/**
 * Saves the typed message unless it is saved or being saved already. While checkout is held, its
 * own save is left to finish.
 */
function saveEnteredMessage() {
  clearTimeout(typingTimer);
  typingTimer = undefined;

  const request = getGiftMessageRequest();
  const isSavedOrSaving =
    request !== null &&
    (currentSave === null ? request.message === savedMessage : request.message === currentSave.message);

  if (request === null || isHoldingCheckout || isSavedOrSaving) {
    updateExpressLock();
    return;
  }
  startSave(request);
}

/**
 * Locks the express checkout buttons while a save is pending: during a pause in typing and until
 * the save in progress ends. It doesn't read the app's widget, which the cart page's redraw
 * removes while the app's save may still be in flight.
 */
function updateExpressLock() {
  const isLocked = typingTimer !== undefined || currentSave !== null;

  // The flag sits on <html>, where the theme's cart redraw doesn't remove it. Its CSS blocks taps
  // and clicks in every browser.
  document.documentElement.toggleAttribute(EXPRESS_LOCK_ATTRIBUTE, isLocked);
  for (const container of document.querySelectorAll(EXPRESS_BUTTONS_SELECTOR)) {
    if (!(container instanceof HTMLElement)) continue;

    // Also blocks keyboard access, from Safari 15.5.
    container.inert = isLocked;
  }
}

/**
 * Submits the cart form to checkout. submit() can't be cancelled by other listeners, and they
 * already received the original submit event.
 * @param {HTMLFormElement} form
 */
function continueCheckout(form) {
  const checkoutField = document.createElement('input');
  checkoutField.type = 'hidden';
  checkoutField.name = 'checkout';
  form.append(checkoutField);
  // Called through the prototype: a form control named "submit" would shadow form.submit().
  HTMLFormElement.prototype.submit.call(form);
  // submit() has already read the fields, so the form returns clean from the back/forward cache.
  checkoutField.remove();
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLButtonElement} submitter
 * @param {GiftMessageRequest} request
 */
async function holdCheckout(form, submitter, request) {
  isHoldingCheckout = true;
  submitter.setAttribute('aria-busy', 'true');

  try {
    // Saved again even when an earlier save of this message is in flight or confirmed: its
    // request may have been lost, and checkout gets the full MAX_HOLD_MS.
    await startSave(request);
  } catch (error) {
    console.error('Gift message safeguard: continuing checkout without a confirmed greeting card', error);
  } finally {
    isHoldingCheckout = false;
    submitter.removeAttribute('aria-busy');
  }

  continueCheckout(form);
}

/** @param {EventTarget | null} target */
function isMessageField(target) {
  return target instanceof HTMLTextAreaElement && target.id === MESSAGE_FIELD_ID;
}

document.addEventListener('input', (event) => {
  if (!isMessageField(event.target)) return;

  clearTimeout(typingTimer);
  typingTimer = setTimeout(saveEnteredMessage, SAVE_WHILE_TYPING_DELAY_MS);
  updateExpressLock();
});

document.addEventListener('focusout', (event) => {
  if (isMessageField(event.target)) saveEnteredMessage();
});

// Ticking the box doesn't make the app save text that is already in the field.
document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.id === CHECKBOX_ID) saveEnteredMessage();
});

// The theme's cart redraw (morph) removes attributes its markup doesn't have, inert included.
// After a quantity change, cart:update is dispatched right before that redraw, so inert is set
// again once it has run.
document.addEventListener(CART_UPDATE_EVENT, () => queueMicrotask(updateExpressLock));

// Listens on window in the bubble phase, after the form's and the document's submit handlers:
// when another script has cancelled checkout, holding and then calling submit() would send the
// customer past it.
window.addEventListener('submit', (event) => {
  const { submitter } = event;
  if (!(submitter instanceof HTMLButtonElement) || submitter.name !== 'checkout') return;
  if (!(event.target instanceof HTMLFormElement)) return;

  if (isHoldingCheckout) {
    event.preventDefault();
    return;
  }
  if (event.defaultPrevented) return;

  const request = getGiftMessageRequest();
  if (request === null) return;

  event.preventDefault();
  holdCheckout(event.target, submitter, request);
});
